import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { DEEP_BORDER_BOX } from 'common';
import buttonStyled from 'components/Button/styles';

const {
  SPACING,
  RADIUS,
  SHADOW_SPREAD,
} = Tokens;

const TOGGLE_BUTTON_HEIGHT = '2rem';
const TOGGLE_BUTTON_INNER_SHADOW_WIDTH = SHADOW_SPREAD.SHADOW_SPREAD_2;
const SMALL_TOGGLE_BUTTON_INNER_SHADOW_WIDTH = '0.063rem'; // There is no border width token
const TOGGLE_BUTTON_MIN_WIDTH = '4rem';

/* Defaults */
const PADDING_1 = `${SPACING.SPACING_0} 0.6875rem ${SPACING.SPACING_0} ${SPACING.SPACING_12}`;
const PADDING_2 = `${SPACING.SPACING_0} ${SPACING.SPACING_12} ${SPACING.SPACING_0} 0.6875rem`;

const BORDER_1 = `${RADIUS.RADIUS_2} ${RADIUS.RADIUS_0} ${RADIUS.RADIUS_0} ${RADIUS.RADIUS_2}`;
const BORDER_2 = `${RADIUS.RADIUS_0} ${RADIUS.RADIUS_2} ${RADIUS.RADIUS_2} ${RADIUS.RADIUS_0}`;

const getBorderStyle = (props) => {
  if (props.isSelected) {
    return `0.0625rem solid ${props.$theme.ON.divider}`;
  }
  return `0.0625rem solid ${props.$theme.OFF.divider}`;
};

const getColor = (props) => {
  if (props.isSelected) {
    if (props.disabled) {
      return props.$theme.ON.disabled.content;
    }
    if (props.$isPressed) {
      return props.$theme.ON.pressed.content;
    }
    return props.$theme.ON.content;
  }
  if (props.disabled) {
    return props.$theme.OFF.disabled.content;
  }
  if (props.$isPressed) {
    return props.$theme.OFF.pressed.content;
  }
  return props.$theme.OFF.content;
};

const TOGGLE_BUTTON_STYLE = css`
  direction: ${props => (props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};
  position: relative;
  display: inline-block;
  outline: none;
  cursor: pointer;
  overflow: hidden;
  height: ${TOGGLE_BUTTON_HEIGHT};
  min-width: ${TOGGLE_BUTTON_MIN_WIDTH};
  max-width: 100%;
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: ${RADIUS.RADIUS_0};
  ${props => !props.isSelected ? css`background: ${props.$theme.OFF.background};` : css`background: ${props.$theme.ON.background};`}
  // Gap between icon and text
  & [data-parent="button"], [data-parent="togglegroupbutton"] {
    ${props => css`margin: ${(props.RTL || props.$rtl || props.rtl) ? `0 0 0 ${SPACING.SPACING_4}` : `0 ${SPACING.SPACING_4} 0 0`};`}
  }
`;

const TOGGLE_BUTTON_FOCUSED = css`
  :focus {
    outline: none;
    box-shadow: inset 0 0 0 ${TOGGLE_BUTTON_INNER_SHADOW_WIDTH} ${props => props.$theme.ON.focused.border};
  }
`;

const SMALL_TOGGLE_BUTTON_FOCUSED = css`
  :focus {
    outline: none;
    box-shadow: inset 0 0 0 ${SMALL_TOGGLE_BUTTON_INNER_SHADOW_WIDTH} ${props => props.$theme.ON.focused.border};
  }
  `;

const TOGGLE_BUTTON_HOVERED = css`
  :hover:not(:disabled) {
    outline: none;
    background-color: ${props => !props.disabled && (!props.isSelected ? props.$theme.OFF.hover.background : props.$theme.ON.hover.background)};
    ${buttonStyled.ButtonText} {
      color:  ${props => !props.disabled && (!props.isSelected ? props.$theme.OFF.hover.content : props.$theme.ON.hover.content)};
    }
  }
`;

const TOGGLE_BUTTON_FOCUSED_HOVERED = css`
  :focus:hover:not(:disabled) {
    outline: none;
    background-color: ${props => !props.disabled && (!props.isSelected ? props.$theme.OFF.hover.background : props.$theme.ON.hover.background)};
    box-shadow: inset 0 0 0 ${TOGGLE_BUTTON_INNER_SHADOW_WIDTH} ${props => props.isSelected ? props.$theme.ON.focusedHovered.border : props.$theme.OFF.focusedHovered.border};
  }
`;

const SMALL_TOGGLE_BUTTON_FOCUSED_HOVERED = css`
  :focus:hover:not(:disabled) {
    outline: none;
    background-color: ${props => !props.disabled && (!props.isSelected ? props.$theme.OFF.hover.background : props.$theme.ON.hover.background)};
    box-shadow: inset 0 0 0 ${SMALL_TOGGLE_BUTTON_INNER_SHADOW_WIDTH} ${props => props.isSelected ? props.$theme.ON.focusedHovered.border : props.$theme.OFF.focusedHovered.border};
  }
`;

const TOGGLE_BUTTON_DISABLED = css`
  :disabled {
    cursor: initial;
    background-color: ${props => props.isSelected ? props.$theme.ON.disabled.background : props.$theme.OFF.disabled.background};
  }
`;

export default {
  GroupWrapper: styled.div`
    ${DEEP_BORDER_BOX}
    display: inline-flex;
    ${({ RTL }) => RTL && css`
      direction: rtl;
    `}
    border: none;
    outline: none;
    border-radius: ${SPACING.SPACING_2};
    & > div:first-child {
      border-radius: ${BORDER_1};
    }
    & > div:last-child {
      border-radius: ${BORDER_2};
    }
    & div:last-child button {
      border: none;
      & > span {
        padding: 0 ${SPACING.SPACING_12} 0 ${SPACING.SPACING_12};
      }
    }
  `,
  ToggleButtonContent: styled.button`
    ${TOGGLE_BUTTON_STYLE}
    & [data-parent="button"] * {
      fill: ${props => getColor(props)};
    }
    > span {
      ${props => !(props.RTL || props.$rtl || props.rtl) ? css`padding: ${PADDING_1};` : css`padding: ${PADDING_2};`}
    }
    &[data-variant="search-w-chips-toggle"] {
      min-width: initial;
      > span {
        padding: 0 ${SPACING.SPACING_12};
      }
      border: none;
    }
    ${props => !(props.RTL || props.$rtl || props.rtl) ? css`border-right: ${getBorderStyle(props)}; ` : css`border-left: ${getBorderStyle(props)}; `}
    & [data-parent="button-text"] {
      color: ${props => getColor(props)};
    }
    ${TOGGLE_BUTTON_FOCUSED}
    ${TOGGLE_BUTTON_HOVERED}
    ${TOGGLE_BUTTON_FOCUSED_HOVERED}
    ${TOGGLE_BUTTON_DISABLED}
`,
  SmallToggleButtonContent: styled.button`
    ${TOGGLE_BUTTON_STYLE}
    & [data-parent="button"] * {
      fill: ${props => getColor(props)};
    }
    > span {
      ${props => !(props.RTL || props.$rtl || props.rtl) ? css`padding: ${PADDING_1};` : css`padding: ${PADDING_2};`}
    }
    ${props => !(props.RTL || props.$rtl || props.rtl) ? css`border-right: ${getBorderStyle(props)}; ` : css`border-left: ${getBorderStyle(props)}; `}
    & [data-parent="button-text"] {
      color: ${props => getColor(props)};
    }
    &[data-variant="search-w-chips-toggle"] {
      color: ${props => getColor(props)};
      min-width: initial;
      max-height: 1.25rem;
      > span {
        padding: 0 ${SPACING.SPACING_8};
      }
      border: none;
    }
    ${SMALL_TOGGLE_BUTTON_FOCUSED}
    ${TOGGLE_BUTTON_HOVERED}
    ${SMALL_TOGGLE_BUTTON_FOCUSED_HOVERED}
    ${TOGGLE_BUTTON_DISABLED} // It is not used in SearchwChipsGlobalLogicButton
`,
  ToggleButtonIconWrapper: styled.div`
  `,
};